import { Box, Divider, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DealerQuotesStatus,
    DealerQuotesAction,
    FullQuotationDataFragment,
    LiveDealerQuotesDataFragment,
} from '../../../../api';
import Accordion from '../../../../components/Accordion';
import useCommonStyles from '../../../../useCommonStyles';
import { DealerQuote, useDealerQuotes } from './hooks/useDealerQuotes';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        '& .MuiListItemIcon-root': {
            minWidth: '40px',
            margin: 'auto',
        },
    },
    inline: {
        display: 'inline',
    },
    notQuotedYet: {
        color: '#1f6b5a',
    },
    dateTime: {
        color: '#1f6b5a',
    },
    newStatus: {
        color: '#856404',
        backgroundColor: '#fbeccb',
    },
    shapeCircle: {
        borderRadius: '50%',
        backgroundColor: '#f1ece0',
        height: '26px',
        width: '26px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
    },
    shapeCircleSubmitted: {
        borderRadius: '50%',
        backgroundColor: '#856404',
        color: '#ffffff',
    },
}));

type DealerQuotesProps = {
    quotation: FullQuotationDataFragment | LiveDealerQuotesDataFragment;
};

const DealerQuotes = ({ quotation }: DealerQuotesProps) => {
    const { t } = useTranslation(['purchaseTransactionsPage', 'common']);
    const classes = useStyles();
    const commonStyles = useCommonStyles();

    const dealerQuotes = useDealerQuotes({
        quotation,
    });

    const renderActionHistory = useCallback(
        (dealer: DealerQuote) => {
            if (!dealer.actionHistory || dealer.actionHistory.length === 0) {
                return null;
            }

            const latestAction = dealer.actionHistory.reduce((latest, current) =>
                new Date(current.actionOn) > new Date(latest.actionOn) ? current : latest
            );

            const latestActionText = `${t(
                `purchaseTransactionsPage:quotation.dealerQuotes.action.${latestAction.action}`
            )} ${t('common:formats.longDateTime', {
                date: new Date(latestAction.actionOn),
            })}`;

            if (latestAction?.action === DealerQuotesAction.Created || dealer?.actionHistory?.length === 1) {
                return (
                    <Typography color="textSecondary" component="span" variant="body2">
                        {latestActionText}
                    </Typography>
                );
            }

            return (
                <Accordion title={latestActionText} titleColor="textSecondary" titleVariant="body2" noBorder>
                    <Box display="flex" flexDirection="column" style={{ rowGap: '4px' }} width="100%">
                        {dealer.actionHistory.map((ac, index) => {
                            const isLatest = index === dealer.actionHistory.length - 1;

                            return (
                                <Box
                                    key={`${ac.action}-${new Date(ac.actionOn).getTime()}`}
                                    alignItems="baseline"
                                    display="flex"
                                    width="100%"
                                >
                                    <Typography
                                        color={isLatest ? 'secondary' : 'textSecondary'}
                                        style={{ width: '90px', margin: '1px 0px 1px' }}
                                        variant="body2"
                                    >
                                        <strong>
                                            {t(`purchaseTransactionsPage:quotation.dealerQuotes.action.${ac.action}`)}
                                        </strong>
                                    </Typography>

                                    <Typography color={isLatest ? 'secondary' : 'textSecondary'} variant="body2">
                                        {t('common:formats.longDateTime', {
                                            date: new Date(ac.actionOn),
                                        })}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Accordion>
            );
        },
        [t]
    );

    const renderStatus = useCallback(
        (dealer: DealerQuote) => {
            // If the dealer has submitted a quote, display the amount
            if (dealer.amount) {
                return (
                    <Typography
                        align="right"
                        className={commonStyles.fontWeightBold}
                        color="textPrimary"
                        variant="body2"
                    >
                        {t('common:currency')}
                        {(dealer.amount || 0).toLocaleString()}
                    </Typography>
                );
            }

            switch (dealer.status) {
                case DealerQuotesStatus.AwaitingInvite:
                    return (
                        <Typography
                            className={`${commonStyles.fontWeightBold} ${commonStyles.rightTextAlign}`}
                            color="textSecondary"
                            variant="body2"
                        >
                            {t(`purchaseTransactionsPage:quotation.dealerQuotes.status.awaitingInvite`)}
                        </Typography>
                    );
                case DealerQuotesStatus.AwaitingQuote:
                    return (
                        <Typography
                            className={`${commonStyles.fontWeightBold} ${commonStyles.rightTextAlign}`}
                            color="textSecondary"
                            variant="body2"
                        >
                            {t(`purchaseTransactionsPage:quotation.dealerQuotes.status.awaitingQuote`)}
                        </Typography>
                    );
                default:
                    return null;
            }
        },
        [commonStyles.fontWeightBold, commonStyles.rightTextAlign, t]
    );

    return (
        <List className={classes.root} style={{ marginTop: '6px' }}>
            {dealerQuotes.length === 0 && (
                <ListItem>
                    <ListItemText primary={t('purchaseTransactionsPage:quotation.inviteQuote.noDealersSelected')} />
                </ListItem>
            )}
            {dealerQuotes.map((dealer, index) => (
                <Fragment key={dealer.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" style={{ display: 'block' }}>
                        <Box alignItems="flex-start" display="flex" justifyContent="space-between" width="100%">
                            <Box display="flex" minWidth={0}>
                                <ListItemIcon>
                                    <div
                                        className={
                                            classes.shapeCircle +
                                            (dealer.status === DealerQuotesStatus.Quoted
                                                ? ` ${classes.shapeCircleSubmitted}`
                                                : '')
                                        }
                                    >
                                        {index + 1}
                                    </div>
                                </ListItemIcon>
                                <ListItemText
                                    primary={dealer.name}
                                    secondary={
                                        dealer.tagNames && (
                                            <Typography color="textSecondary" component="span" variant="body2">
                                                {dealer.tagNames}
                                            </Typography>
                                        )
                                    }
                                />
                            </Box>
                            <Box mt={1} style={{ flexShrink: 0, whiteSpace: 'nowrap' }} textAlign="right">
                                {renderStatus(dealer)}
                            </Box>
                        </Box>
                        <Box ml={5}>{renderActionHistory(dealer)}</Box>
                    </ListItem>
                </Fragment>
            ))}
        </List>
    );
};

export default DealerQuotes;
